import { useCallback, useEffect, useState } from "react";
import { BarChart3, Users, Eye, TrendingUp, AlertTriangle, MousePointerClick, UserPlus, FileText, BadgeDollarSign, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { safeCopy } from "@/lib/api";

const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase";

const UTM_CHANNELS = [
  { source: "discord", medium: "server-post", label: "Discord server post" },
  { source: "discord", medium: "dm", label: "Discord DM" },
  { source: "reddit", medium: "post", label: "Reddit post" },
  { source: "tiktok", medium: "video", label: "TikTok video / bio" },
  { source: "youtube", medium: "description", label: "YouTube description" },
  { source: "twitter", medium: "tweet", label: "Twitter / X" },
  { source: "facebook", medium: "group-post", label: "Facebook group" },
  { source: "fivem-forums", medium: "thread", label: "FiveM / game forums" },
  { source: "email", medium: "signature", label: "Email signature" },
];

function UtmLinkKit() {
  const [path, setPath] = useState("/join-creators");
  const [campaign, setCampaign] = useState("founding-push");
  const [copied, setCopied] = useState("");
  const origin = window.location.origin;

  const buildLink = (c) => `${origin}${path}?utm_source=${c.source}&utm_medium=${c.medium}&utm_campaign=${encodeURIComponent(campaign)}`;

  const copy = async (key, link) => {
    if (await safeCopy(link)) {
      setCopied(key);
      toast.success("Link copied");
      setTimeout(() => setCopied(""), 1500);
    } else {
      toast.error("Copy blocked");
    }
  };

  return (
    <div data-testid="utm-link-kit" className="border border-white/10 bg-black/40 p-5">
      <div className="flex items-center gap-2 mb-1">
        <Link2 className="w-4 h-4 text-nc-blue" />
        <p className={labelCls}>UTM link kit — tag every link you share</p>
      </div>
      <p className="font-mono text-[9px] text-zinc-600 mb-4 leading-relaxed">
        Post these per channel. The UTM campaigns table on this page then shows exactly which channel sends real traffic — stop guessing, double down on what works.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className={labelCls}>Landing page</label>
          <select data-testid="utm-path-select" value={path} onChange={(e) => setPath(e.target.value)} className="w-full mt-1.5 bg-[#050505] border border-white/10 px-3 py-2 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue">
            <option value="/join-creators">/join-creators (Creator Program)</option>
            <option value="/">/ (Homepage)</option>
            <option value="/services">/services (Client work)</option>
            <option value="/marketplace">/marketplace</option>
            <option value="/join-sales">/join-sales (Sales team)</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Campaign tag</label>
          <input data-testid="utm-campaign-input" value={campaign} onChange={(e) => setCampaign(e.target.value.slice(0, 40))} className="w-full mt-1.5 bg-[#050505] border border-white/10 px-3 py-2 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue" />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        {UTM_CHANNELS.map((c) => {
          const key = `${c.source}-${c.medium}`;
          const link = buildLink(c);
          return (
            <div key={key} data-testid={`utm-row-${key}`} className="flex items-center gap-3 border border-white/5 bg-[#050505] px-3 py-2">
              <p className="font-mono text-[10px] text-zinc-300 w-40 shrink-0">{c.label}</p>
              <code className="font-mono text-[9px] text-zinc-600 truncate flex-1">{link}</code>
              <button data-testid={`utm-copy-${key}`} onClick={() => copy(key, link)} className="font-mono text-[8px] uppercase tracking-[0.15em] border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 px-2.5 py-1 transition-colors shrink-0 flex items-center gap-1">
                {copied === key ? <Check className="w-3 h-3" /> : null}{copied === key ? "Copied" : "Copy"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent = "text-zinc-50", testid }) {
  return (
    <div data-testid={testid} className="border border-white/10 bg-black/40 px-5 py-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5 text-nc-blue" strokeWidth={1.75} />
        <p className={labelCls}>{label}</p>
      </div>
      <p className={`font-sans font-bold text-2xl ${accent}`}>{value}</p>
    </div>
  );
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/analytics/overview?days=${days}`).then((r) => setData(r.data)).catch(() => {});
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div data-testid="admin-analytics" className="border border-white/10 p-10 text-center font-mono text-xs text-zinc-600">Loading analytics…</div>;

  const t = data.totals;
  const maxPv = Math.max(1, ...data.series.map((s) => s.pageviews));
  const maxFunnel = Math.max(1, ...data.funnel.map((f) => f.n));

  return (
    <div data-testid="admin-analytics" className="flex flex-col gap-6 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-sans font-bold uppercase text-lg text-zinc-50 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-nc-blue" /> Website Analytics</h2>
          <p className="font-mono text-[10px] text-zinc-500 mt-1">First-party tracking — cookieless, IPs hashed. Data starts collecting from deployment of this system.</p>
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`analytics-range-${d}`} onClick={() => setDays(d)} className={`font-mono text-[10px] px-3 py-1.5 border transition-colors ${days === d ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
              {d}D
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label={`Visitors (${days}d)`} value={t.unique_visitors.toLocaleString()} testid="analytics-visitors" />
        <StatCard icon={Eye} label={`Pageviews (${days}d)`} value={t.pageviews.toLocaleString()} testid="analytics-pageviews" />
        <StatCard icon={TrendingUp} label="Visitors today" value={t.today_visitors.toLocaleString()} accent="text-nc-green" testid="analytics-today" />
        <StatCard icon={MousePointerClick} label="Pageviews today" value={t.today_pageviews.toLocaleString()} testid="analytics-today-pv" />
      </div>

      {/* daily chart */}
      <div className="border border-white/10 bg-black/40 p-5">
        <p className={`${labelCls} mb-4`}>Daily traffic</p>
        {data.series.length === 0 ? (
          <p className="font-mono text-[10px] text-zinc-600">No pageviews recorded in this window yet — traffic data appears here as people browse the site.</p>
        ) : (
          <div data-testid="analytics-chart" className="flex items-end gap-[3px] h-32">
            {data.series.map((s) => (
              <div key={s.date} className="flex-1 h-full flex flex-col justify-end group relative">
                <div className="bg-nc-blue/70 hover:bg-nc-blue transition-colors" style={{ height: `${Math.max(3, (s.pageviews / maxPv) * 100)}%` }} />
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black border border-white/20 px-2 py-1 font-mono text-[8px] text-zinc-300 whitespace-nowrap z-10">
                  {s.date.slice(5)} — {s.pageviews} pv / {s.visitors} vis
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* funnel + bottleneck */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
        <div className="border border-white/10 bg-black/40 p-5">
          <p className={`${labelCls} mb-4`}>Conversion funnel ({days}d)</p>
          <div className="flex flex-col gap-2.5">
            {data.funnel.map((f, i) => (
              <div key={f.step} data-testid={`funnel-step-${i}`}>
                <div className="flex justify-between mb-1">
                  <p className="font-mono text-[10px] text-zinc-400">{f.step}</p>
                  <p className="font-mono text-[10px] text-zinc-200">{f.n.toLocaleString()}{i > 0 && data.funnel[i - 1].n > 0 ? ` (${Math.round((f.n / data.funnel[i - 1].n) * 100)}%)` : ""}</p>
                </div>
                <div className="h-2 bg-white/5"><div className={`h-full ${i === data.funnel.length - 1 ? "bg-nc-green" : "bg-nc-blue/70"}`} style={{ width: `${Math.max(2, (f.n / maxFunnel) * 100)}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-nc-orange/40 bg-nc-orange/5 p-5">
          <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase flex items-center gap-1.5 mb-3"><AlertTriangle className="w-3.5 h-3.5" /> Biggest bottleneck</p>
          {data.bottleneck ? (
            <>
              <p className="font-sans font-bold text-sm text-zinc-100">{data.bottleneck.from} → {data.bottleneck.to}</p>
              <p data-testid="analytics-bottleneck" className="font-sans font-bold text-3xl text-nc-orange mt-2">{data.bottleneck.drop}%</p>
              <p className="font-mono text-[9px] text-zinc-500 mt-2 leading-relaxed">of people drop off at this step. Improving this transition is your highest-leverage move.</p>
            </>
          ) : (
            <p className="font-mono text-[10px] text-zinc-500">Not enough data yet to compute drop-offs.</p>
          )}
        </div>
      </div>

      {/* conversions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={UserPlus} label="Signups" value={t.signups.toLocaleString()} testid="analytics-signups" />
        <StatCard icon={FileText} label="Leads" value={t.leads.toLocaleString()} testid="analytics-leads" />
        <StatCard icon={FileText} label="Quote briefs" value={t.quotes.toLocaleString()} testid="analytics-quotes" />
        <StatCard icon={BadgeDollarSign} label="Creator activations" value={t.activations.toLocaleString()} accent="text-nc-green" testid="analytics-activations" />
      </div>

      <UtmLinkKit />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border border-white/10 bg-black/40 p-5">
          <p className={`${labelCls} mb-3`}>Top pages</p>
          {data.top_pages.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No data yet.</p>}
          {data.top_pages.map((p) => (
            <div key={p.path} className="flex justify-between border-b border-white/5 py-1.5">
              <p className="font-mono text-[10px] text-zinc-300 truncate mr-3">{p.path}</p>
              <p className="font-mono text-[10px] text-nc-blue shrink-0">{p.n}</p>
            </div>
          ))}
        </div>
        <div className="border border-white/10 bg-black/40 p-5">
          <p className={`${labelCls} mb-3`}>Traffic sources (referrers)</p>
          {data.top_referrers.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No data yet.</p>}
          {data.top_referrers.map((r) => (
            <div key={r.source} className="flex justify-between border-b border-white/5 py-1.5">
              <p className="font-mono text-[10px] text-zinc-300 truncate mr-3">{r.source}</p>
              <p className="font-mono text-[10px] text-nc-blue shrink-0">{r.n}</p>
            </div>
          ))}
        </div>
        <div className="border border-white/10 bg-black/40 p-5">
          <p className={`${labelCls} mb-3`}>UTM campaigns</p>
          {data.utm_sources.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No UTM-tagged traffic yet — add ?utm_source=discord to your links to see which channels perform.</p>}
          {data.utm_sources.map((r) => (
            <div key={r.source} className="flex justify-between border-b border-white/5 py-1.5">
              <p className="font-mono text-[10px] text-zinc-300 truncate mr-3">{r.source}</p>
              <p className="font-mono text-[10px] text-nc-blue shrink-0">{r.n}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
